import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { researchApi } from '@/lib/supabase';
import { useLocalizedContent } from '@/hooks/useLocalizedContent';
import { optimizeImageUrl } from '@/lib/imageOptimize';

const ResearchDetailPage = () => {
  const { slug } = useParams();
  const [research, setResearch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { t } = useTranslation();
  const { getField, getPhases, isArabic } = useLocalizedContent();

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const data = await researchApi.getBySlug(slug);
        setResearch(data);
      } catch (err) {
        console.error('Error fetching project:', err);
        setError('Project not found');
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !research) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] px-6 md:px-12 lg:px-24 py-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="heading-large mb-4">{isArabic ? 'البحث غير موجود' : 'Research not found'}</h1>
          <Link to="/research" className="caption link-subtle">
            {isArabic ? 'العودة إلى البحث' : 'Return to Research'}
          </Link>
        </div>
      </div>
    );
  }

  const phases = getPhases(research);
  // Use English phases for images (images are shared)
  const enPhases = research.phases || [];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % phases.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + phases.length) % phases.length);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Back Navigation */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="px-6 md:px-12 lg:px-24 pt-24 pb-12"
      >
        <Link 
          to="/research" 
          className="inline-flex items-center gap-2 caption text-[#1C1C1C] hover:text-[#8B6F47] transition-colors"
        >
          <ArrowLeft size={14} className={isArabic ? 'rotate-180' : ''} /> {isArabic ? 'العودة إلى البحث' : 'Back to Research'}
        </Link>
      </motion.div>

      {/* Header */}
      <div className="px-6 md:px-12 lg:px-24 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl"
        >
          <span className="caption text-[#1C1C1C] block mb-4">
            {research.year} — {research.status}
          </span>
          <h1 className="heading-display mb-6" data-testid="research-title">
            {getField(research, 'title')}
          </h1>
          <p className="body-large text-[#1C1C1C]">
            {getField(research, 'short_description')}
          </p>
        </motion.div>
      </div>

      {/* Image Slideshow - uses English phases for images */}
      {enPhases.length > 0 && (
        <div className="px-6 md:px-12 lg:px-24 mb-12">
          <div className="max-w-5xl mx-auto">
            <div className="relative aspect-[16/9] bg-[#F5F2EB] border border-[#E5E0D6] overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentSlide}
                  src={optimizeImageUrl(enPhases[currentSlide]?.image || '', 1200)}
                  alt={phases[currentSlide]?.title || ''}
                  className="w-full h-full object-cover grayscale"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                />
              </AnimatePresence>

              {enPhases.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-[#FDFBF7] hover:bg-[#1C1C1C] hover:text-white border border-[#E5E0D6] flex items-center justify-center transition-colors"
                    aria-label="Previous slide"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-[#FDFBF7] hover:bg-[#1C1C1C] hover:text-white border border-[#E5E0D6] flex items-center justify-center transition-colors"
                    aria-label="Next slide"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}

              {enPhases.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {enPhases.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2 h-2 transition-colors ${
                        index === currentSlide ? 'bg-[#1C1C1C]' : 'bg-[#E5E0D6]'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Phase Content */}
      {phases.length > 0 && (
        <div className="px-6 md:px-12 lg:px-24 pb-24">
          <div className="max-w-3xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="heading-medium mb-6">
                  {phases[currentSlide]?.title}
                </h2>
                <div className="space-y-4">
                  {phases[currentSlide]?.description?.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="font-sans text-lg leading-relaxed text-[#1C1C1C]">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Phase Navigation */}
            {phases.length > 1 && (
              <div className="mt-12 pt-8 border-t border-[#E5E0D6] flex justify-between items-center">
                <button
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  className={`caption flex items-center gap-2 transition-colors ${
                    currentSlide === 0 
                      ? 'text-[#E5E0D6] cursor-not-allowed' 
                      : 'text-[#1C1C1C] hover:text-[#8B6F47]'
                  }`}
                >
                  <ChevronLeft size={14} /> {isArabic ? 'المرحلة السابقة' : 'Previous Phase'}
                </button>
                <span className="caption text-[#1C1C1C]">
                  {currentSlide + 1} / {phases.length}
                </span>
                <button
                  onClick={nextSlide}
                  disabled={currentSlide === phases.length - 1}
                  className={`caption flex items-center gap-2 transition-colors ${
                    currentSlide === phases.length - 1
                      ? 'text-[#E5E0D6] cursor-not-allowed' 
                      : 'text-[#1C1C1C] hover:text-[#8B6F47]'
                  }`}
                >
                  {isArabic ? 'المرحلة التالية' : 'Next Phase'} <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchDetailPage;
